import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Button,
  Box,
  IconButton,
  InputAdornment,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Close,
  AttachMoney,
  Message,
  Send,
} from '@mui/icons-material';
import { motion } from 'framer-motion';

interface OfferPopupProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (offerData: { price: number; message: string }) => Promise<void>;
  productTitle: string;
  productPrice: number;
  sellerName: string;
  loading?: boolean;
  // Optional edit mode support
  mode?: 'create' | 'edit';
  initialPrice?: number;
  initialMessage?: string;
  offerId?: string;
}

const OfferPopup: React.FC<OfferPopupProps> = ({
  open,
  onClose,
  onSubmit,
  productTitle,
  productPrice,
  sellerName,
  loading = false,
  mode = 'create',
  initialPrice,
  initialMessage,
  offerId,
}) => {
  const [offerPrice, setOfferPrice] = useState<number>(initialPrice || 0);
  const [message, setMessage] = useState(initialMessage || '');
  const [errors, setErrors] = useState<{ price?: string; message?: string }>({});

  // If dialog opens in edit mode, prime form with initial values
  React.useEffect(() => {
    if (open) {
      setOfferPrice(initialPrice || 0);
      setMessage(initialMessage || '');
      setErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialPrice, initialMessage]);

  const handlePriceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(event.target.value) || 0;
    setOfferPrice(value);
    
    // Clear price error when user starts typing
    if (errors.price) {
      setErrors(prev => ({ ...prev, price: undefined }));
    }
  };

  const handleMessageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMessage(event.target.value);
    
    // Clear message error when user starts typing
    if (errors.message) {
      setErrors(prev => ({ ...prev, message: undefined }));
    }
  };

  const validateForm = () => {
    const newErrors: { price?: string; message?: string } = {};

    if (offerPrice <= 0) {
      newErrors.price = 'Please enter a valid offer price';
    }

    if (offerPrice > productPrice) {
      newErrors.price = 'Your offer cannot be higher than the listed price';
    }

    if (!message.trim()) {
      newErrors.message = 'Please include a message with your offer';
    }

    if (message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters long';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      await onSubmit({
        price: offerPrice,
        message: message.trim(),
      });
      
      // Reset form
      setOfferPrice(0);
      setMessage('');
      setErrors({});
    } catch (error) {
      console.error('Error submitting offer:', error);
    }
  };

  const handleClose = () => {
    setOfferPrice(initialPrice || 0);
    setMessage(initialMessage || '');
    setErrors({});
    onClose();
  };

  const savings = productPrice - offerPrice;
  const savingsPercentage = productPrice > 0 ? (savings / productPrice) * 100 : 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        pb: 1,
        borderBottom: '1px solid #e0e0e0'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AttachMoney sx={{ color: '#667eea', fontSize: 28 }} />
          <Typography variant="h6" fontWeight="bold">
            {mode === 'edit' ? 'Edit Offer' : 'Make an Offer'}
          </Typography>
          {mode === 'edit' && (
            <Typography variant="caption" sx={{ 
              bgcolor: 'grey.100', 
              px: 1, 
              py: 0.5, 
              borderRadius: 1,
              fontFamily: 'monospace',
              fontSize: '0.7rem'
            }}>
              ID: {offerId ? offerId.substring(0, 8) + '...' : 'Unknown'}
            </Typography>
          )}
        </Box>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            color: 'text.secondary',
            '&:hover': {
              bgcolor: 'rgba(0,0,0,0.04)',
            }
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
            You're making an offer for:
          </Typography>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
            {productTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Listed price: <strong>${productPrice}</strong> • Seller: {sellerName}
          </Typography>
        </Box>

        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Your Offer Price"
            type="number"
            value={offerPrice || ''}
            onChange={handlePriceChange}
            error={!!errors.price}
            helperText={errors.price}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AttachMoney />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          {offerPrice > 0 && (
            <Box sx={{ 
              p: 2, 
              bgcolor: savings > 0 ? 'success.50' : 'warning.50',
              borderRadius: 1,
              border: `1px solid ${savings > 0 ? 'success.200' : 'warning.200'}`,
              mb: 2
            }}>
              <Typography variant="body2" color="text.secondary">
                {savings > 0 ? (
                  <>
                    You're offering <strong>${savings.toFixed(2)} less</strong> than the listed price
                    <br />
                    <strong>{savingsPercentage.toFixed(1)}% discount</strong>
                  </>
                ) : savings < 0 ? (
                  <>
                    You're offering <strong>${Math.abs(savings).toFixed(2)} more</strong> than the listed price
                  </>
                ) : (
                  "You're offering the exact listed price"
                )}
              </Typography>
            </Box>
          )}

          <TextField
            fullWidth
            label="Message to Seller"
            multiline
            rows={4}
            value={message}
            onChange={handleMessageChange}
            error={!!errors.message}
            helperText={errors.message || "Explain why you're interested and any additional details"}
            placeholder="Hi! I'm interested in this product because..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                  <Message />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Note:</strong> Your offer will be sent as a message to the seller. 
            They can accept, decline, or counter your offer.
          </Typography>
        </Alert>
      </DialogContent>

      <DialogActions sx={{ 
        px: 3, 
        pb: 3, 
        gap: 2,
        flexDirection: { xs: 'column', sm: 'row' },
        '& > *': {
          flex: { xs: 1, sm: 'none' },
          minWidth: { xs: '100%', sm: 'auto' }
        }
      }}>
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={loading}
          sx={{
            textTransform: 'none',
            fontWeight: 'bold',
            py: 1.5,
            px: 3,
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || offerPrice <= 0 || !message.trim()}
          startIcon={loading ? <CircularProgress size={16} /> : <Send />}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            textTransform: 'none',
            fontWeight: 'bold',
            py: 1.5,
            px: 3,
            boxShadow: 'none',
            '&:hover': {
              background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
            },
            '&:disabled': {
              background: 'rgba(0,0,0,0.12)',
              color: 'rgba(0,0,0,0.26)',
            }
          }}
        >
          {loading ? (mode === 'edit' ? 'Saving...' : 'Sending Offer...') : (mode === 'edit' ? 'Save Changes' : 'Send Offer')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OfferPopup;
